import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import updateCustomer from '@/shared/api/customers/updateCustomer';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import Customer from '@/shared/types/customer.type';

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type Args = {
  customerId: string;
  onUpdateNotificationBar: OnUpdateNotificationBar;
};

const useCustomerEditOperations = ({
  customerId,
  onUpdateNotificationBar,
}: Args) => {
  const { t } = useTranslation(['customers', 'common']);

  const [saveInProgress, setSaveInProgress] = useState(false);

  const handleSaveCustomer = async ({
    accountDetailsFormFields,
    billingAddressFormFields,
    deliveryAddressFormFields,
  }: {
    accountDetailsFormFields: Partial<Customer>;
    billingAddressFormFields: Partial<Customer>;
    deliveryAddressFormFields: Partial<Customer> & {
      billing_same_as_delivery: boolean;
    };
  }) => {
    let data: Partial<Customer> = {};

    if (deliveryAddressFormFields.billing_same_as_delivery) {
      data = {
        ...accountDetailsFormFields,
        ...deliveryAddressFormFields,
        billing_address: deliveryAddressFormFields.delivery_address,
        billing_address2: deliveryAddressFormFields.delivery_address2,
        billing_city: deliveryAddressFormFields.delivery_city,
        billing_country: deliveryAddressFormFields.delivery_country,
        billing_state: deliveryAddressFormFields.delivery_state,
        billing_postcode: deliveryAddressFormFields.delivery_postcode,
      };
    } else {
      data = {
        ...accountDetailsFormFields,
        ...billingAddressFormFields,
        ...deliveryAddressFormFields,
      };
    }

    setSaveInProgress(true);
    const response = await updateCustomer(customerId, data);

    if (response.success === true) {
      onUpdateNotificationBar({
        open: true,
        message: t('customers:customerWasSuccessfullyUpdated'),
        severity: 'success',
      });
      setSaveInProgress(false);
    } else {
      onUpdateNotificationBar({
        open: true,
        message: response.msg,
        severity: 'error',
      });
      setSaveInProgress(false);
    }
  };

  return {
    saveInProgress,
    handleSaveCustomer,
  };
};

export default useCustomerEditOperations;
